import React from 'react';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { canBuyOffer, isFreeOffer } from '../../../_helpers/skinTryOn.js';
import { SkinPreview } from './ListenerProfileCard.jsx';
import SkinAvailability from './SkinAvailability.jsx';
import SkinPriceTag, { SALE_END_RETRIES, SALE_END_RETRY_MS, skinPriceLabel } from './SkinPriceTag.jsx';
import SkinTimeLimit, { useOfferClocks, useWindowExpired } from './SkinTimeLimit.jsx';
import { useCountdown } from '../../../hooks/useCountdown.js';
import { shopTypeLabel } from '../../../_helpers/shopPreview.js';
import { ShopRarityTag } from '../shop/ShopPreview.jsx';
import '../../../styles/SkinTryOnPanel.css';

/** Purchase error codes with their own words; anything else is the generic line. */
const BUY_ERRORS = new Set([
  'card_declined',
  'skin_sold_out',
  'skin_already_owned',
  'skin_not_for_sale',
  'skin_not_found',
  'payment_provider_disabled',
  'queue_payment_unavailable',
  'skin_refunded',
  'skin_pending',
  'skin_is_free',
]);

/** The locale key for a purchase error code ("skin_sold_out" -> "skinSoldOut"). */
export const skinBuyError = (code) => (BUY_ERRORS.has(code) ? code.replace(/_([a-z])/g, (_, c) => c.toUpperCase()) : 'generic');

/** A skin's price for display ("Free" at 0); it lives with SkinPriceTag (ENG-832). */
export { skinPriceLabel };

/**
 * What a purchase that didn't go through says (useSkinPurchase's `{ error,
 * priceChange }`): "The price of <skin> changed from $X to $Y since you opened
 * it." or "<skin> is no longer for sale." when the price moved (ENG-827),
 * else the error's own line. When the price shown was a sale price and it
 * went up (`wasOnSale`, ENG-832), the sale ended: "The sale on <skin> ended.
 * It's now $Y." A price that went down (a sale starting) keeps the
 * price-changed line.
 */
export const skinPurchaseError = (t, format, purchase) => {
  const change = purchase?.priceChange;
  if (change) {
    if (change.toCents === null) {
      return change.name
        ? t('stream.tryOn.noLongerForSale', { name: change.name })
        : t('stream.tryOn.errors.skinNotForSale');
    }
    const prices = { from: skinPriceLabel(t, format, change.fromCents), to: skinPriceLabel(t, format, change.toCents) };
    if (change.wasOnSale && change.toCents > change.fromCents) {
      return change.name
        ? t('stream.tryOn.saleEnded', { name: change.name, to: prices.to })
        : t('stream.tryOn.saleEndedUnnamed', { to: prices.to });
    }
    return change.name
      ? t('stream.tryOn.priceChanged', { name: change.name, ...prices })
      : t('stream.tryOn.priceChangedUnnamed', prices);
  }
  return t(`stream.tryOn.errors.${skinBuyError(purchase?.error)}`);
};

/**
 * The try-on panel at the bottom of the stream chat, above the message box
 * (Skins, mockup 36, ENG-737): "Trying on Event Horizon / Only you see it. It
 * comes off when the song ends.", the LIMITED · RUN OF 250 tag, what's left
 * ("12 left · #489 of 250") or the Sold out badge once the offer is in
 * (SkinAvailability, ENG-833), and Get it and Take it off.
 *
 * `tryOn` is the skin on (useSkinTryOn), `offer` its `{ status, offer }`.
 * Get it shows for any skin that is for sale and not sold out; it is off
 * while a purchase is under way. Skin sales are platform charges (ENG-818),
 * so the band's payment setup doesn't matter, and band members can buy on
 * their own band's page too (ENG-819).
 * `purchase` is `{ state, error, priceChange }` from useSkinPurchase.
 * `onGetIt(skin, offer)` gets the offer on screen, whose `priceCents` the
 * purchase sends as `expected_price_cents`. A free skin (priceCents 0,
 * ENG-827) reads "Get it free".
 *
 * On sale (ENG-832) the offer's price shows as SkinPriceTag: the regular price
 * struck through, the sale price, the Sale badge and its end. Once the sale
 * ends, Get it turns off and `onOfferEnd(skinId)` asks for the offer again
 * (useSkinTryOn's `refresh`); Get it still sends the offer's `priceCents`.
 *
 * A time-limited item (ENG-834) shows when it stops being for sale
 * (SkinTimeLimit: "Available until Dec 7", then a live "Ends in 5h 12m"); a
 * sale ending at the same moment shows only the sale's end line. When the
 * window closes on screen, Get it turns off at once with "No longer
 * available" and the offer is asked for again (`onOfferEnd`); the panel stays
 * open with that status and Take it off. A purchase already under way is left
 * to the backend. An offer not for sale yet ("coming soon", `availableFrom`)
 * reads "Available from <date>" with no Get it, and is asked for again when
 * that time comes.
 *
 * `variant="shop"` is the skins shop's look (ENG-894), for its hero banner
 * and its sticky try-on panel: `stage` (the skin on the viewer's card or
 * name) under the "Trying on … · this song" pill, then "Trying it on", the
 * name and rarity, the type and what's left, the note, the price (with any
 * sale), the time limit, Get it and Take it off. Only the look changes: the
 * price guard, sold-out, coming-soon and offer-end handling are the same.
 */
const SkinTryOnPanel = ({ tryOn, offer = null, purchase = null, onGetIt, onTakeOff, onOfferEnd = null, variant = 'stream', stage = null }) => {
  const { t, format } = useLocale();
  const details = tryOn && offer?.status === 'ready' ? offer.offer : null;
  const skinId = tryOn?.skin.id ?? null;
  const reread = () => onOfferEnd?.(skinId);
  const until = details?.forSale ? details.availableUntil ?? null : null;
  const clocks = useOfferClocks(details?.sale ?? null, until, reread);
  const expired = useWindowExpired(skinId, clocks.windowClock.ended);
  const comingSoon = details && !details.forSale ? details.availableFrom ?? null : null;
  const opensClock = useCountdown(comingSoon, { onEnd: reread, repeatMs: SALE_END_RETRY_MS, maxRepeats: SALE_END_RETRIES });
  if (!tryOn) return null;
  const { skin } = tryOn;
  const editionSize = details?.editionSize ?? skin.editionSize;
  const buyable = canBuyOffer(details);
  const state = purchase?.state ?? 'idle';
  const busy = state === 'buying' || state === 'redirecting';

  let note = null;
  if (details?.soldOut) note = t('stream.tryOn.soldOut');
  else if (details && !details.forSale && expired) note = t('shop.availability.ended');
  else if (comingSoon && !opensClock.ended) note = t('shop.availability.from', { date: format.dateTime(comingSoon, 'short') });
  else if (details && !details.forSale) note = t('stream.tryOn.notForSale');

  let status = null;
  if (state === 'buying') status = t('stream.tryOn.buying');
  else if (state === 'redirecting') status = t('stream.tryOn.redirecting');
  else if (purchase?.error) status = skinPurchaseError(t, format, purchase);

  const getItButton = buyable && (
    <button
      type="button"
      className="skin-try-on-action skin-try-on-action--primary"
      onClick={() => onGetIt?.(skin, details)}
      disabled={busy || clocks.ended}
      data-testid="skin-try-on-get"
    >
      {isFreeOffer(details)
        ? t('stream.tryOn.getItFree')
        : t('stream.tryOn.getIt', { price: skinPriceLabel(t, format, details.priceCents) })}
    </button>
  );
  const takeOffButton = (
    <button type="button" className="skin-try-on-action" onClick={onTakeOff} disabled={state === 'redirecting'}>
      {t('stream.tryOn.takeOff')}
    </button>
  );

  if (variant === 'shop') {
    return (
      <section className="skin-try-on skin-try-on--shop" data-testid="skin-try-on" aria-label={t('stream.tryOn.label', { name: skin.name })}>
        <div className="skin-try-on-stage vinyl-sleeve-trigger">
          <span className="skin-try-on-pill" data-testid="skin-try-on-pill">{t('stream.nowPlaying.tryOn', { name: skin.name })}</span>
          {stage}
        </div>
        <div className="skin-try-on-info">
          <p className="skin-try-on-kicker">{t('shop.look.tryingOn')}</p>
          <div className="skin-try-on-name-row">
            <h2 className="skin-try-on-name">{skin.name}</h2>
            <ShopRarityTag rarity={skin.rarity} />
          </div>
          <p className="skin-try-on-type">
            <span>{shopTypeLabel(t, skin)}</span>
            {details && <SkinAvailability offer={details} />}
          </p>
          {editionSize && (
            <span className="skin-try-on-tag" data-testid="skin-try-on-tag">
              {t('stream.tryOn.limitedRun', { size: format.number(editionSize) })}
            </span>
          )}
          {details?.forSale && (
            <SkinPriceTag
              priceCents={details.priceCents}
              sale={details.sale}
              clock={clocks.saleClock}
              windowEnds={clocks.shared}
              className="skin-try-on-price"
              testId="skin-try-on-price"
            />
          )}
          {!clocks.shared && !details?.soldOut && (
            <SkinTimeLimit until={until} clock={clocks.windowClock} testId="skin-try-on-time-limit" />
          )}
          <p className="skin-try-on-body">{t('stream.tryOn.onlyYou')}</p>
          {(note || status) && (
            <p className="skin-try-on-status" role="status" data-testid="skin-try-on-status">{status || note}</p>
          )}
          <div className="skin-try-on-actions">
            {getItButton}
            {takeOffButton}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="skin-try-on" data-testid="skin-try-on" aria-label={t('stream.tryOn.label', { name: skin.name })}>
      <div className="skin-try-on-head">
        <SkinPreview cardSkin={{ skin }} large />
        <div className="skin-try-on-text">
          <h3 className="skin-try-on-title">{t('stream.tryOn.title', { name: skin.name })}</h3>
          <p className="skin-try-on-body">{t('stream.tryOn.onlyYou')}</p>
          {editionSize && (
            <span className="skin-try-on-tag" data-testid="skin-try-on-tag">
              {t('stream.tryOn.limitedRun', { size: format.number(editionSize) })}
            </span>
          )}
          {details?.sale && (
            <SkinPriceTag
              priceCents={details.priceCents}
              sale={details.sale}
              clock={clocks.saleClock}
              windowEnds={clocks.shared}
              className="skin-try-on-price"
              testId="skin-try-on-price"
            />
          )}
          {!clocks.shared && !details?.soldOut && (
            <SkinTimeLimit until={until} clock={clocks.windowClock} testId="skin-try-on-time-limit" />
          )}
          {details && <SkinAvailability offer={details} />}
        </div>
      </div>
      {(note || status) && (
        <p className="skin-try-on-status" role="status" data-testid="skin-try-on-status">{status || note}</p>
      )}
      <div className="skin-try-on-actions">
        {getItButton}
        {takeOffButton}
      </div>
    </section>
  );
};

export default SkinTryOnPanel;
